import { Box } from '@mui/material';
import { GridContainer, GridItem } from 'components/atomic/atoms/grid';
import Image from 'next/image';
import type { ReactNode } from 'react';
import { effects } from 'design-system/tokens/effects';
import { bentoTokens } from '../bento-tokens';
import { renderSeeAllButton } from './shared-helpers';

interface SplitStateProps {
  images: string[];
  getAlt: (index: number) => string;
  onImageClick?: (index: number) => void;
  blur?: boolean;
  badgesSlot?: ReactNode;
  seeAllSlot?: (total: number) => ReactNode;
  mapSlot?: ReactNode;
  onSeeAllClick?: () => void;
  total: number;
}

/**
 * Bento Split State (N=2 or 3 Images)
 * 
 * Layout: 60/40 vertical split
 * - Left: Main Hero Image (full height)
 * - Right: Stack of remaining images
 * - N=2: Image 2 + Map (50% each)
 * - N=3: Image 2 + Image 3 (50% each)
 */
export function BentoSplitState({
  images,
  getAlt,
  onImageClick,
  blur,
  badgesSlot,
  seeAllSlot,
  mapSlot,
  total,
}: SplitStateProps) {
  const mainImage = images[0];
  const sideImages = images.slice(1);

  return (
    <GridContainer
      data-testid="gallery-bento-split"
      columnSpacing={bentoTokens.gap}
      sx={{
        filter: blur ? effects.blur[100] : 'none',
      }}
    >
      {/* Left: Main Hero Image (60%) */}
      <GridItem xs={bentoTokens.gridColumns.splitMain}>
        <Box
          onClick={() => onImageClick?.(0)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && onImageClick?.(0)}
          sx={{
            position: 'relative',
            width: '100%',
            height: bentoTokens.height,
            borderRadius: bentoTokens.borderRadius.main,
            overflow: 'hidden',
            cursor: 'pointer',
          }}
        >
          <Image
            src={mainImage}
            alt={getAlt(0)}
            fill
            sizes={bentoTokens.imageSizes.splitMain}
            style={{ objectFit: 'cover' }}
            priority
          />
          {badgesSlot && (
            <Box
              sx={{
                position: 'absolute',
                top: bentoTokens.overlay.badges,
                left: bentoTokens.overlay.badges,
                zIndex: bentoTokens.zIndex.badges,
              }}
            >
              {badgesSlot}
            </Box>
          )}
        </Box>
      </GridItem>

      {/* Right: Sidebar Stack (40%) */}
      <GridItem xs={bentoTokens.gridColumns.splitSide}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: bentoTokens.gap,
            height: bentoTokens.height,
          }}
        >
          {sideImages.map((url, index) => {
            const imageIndex = index + 1;
            const isLast = index === sideImages.length - 1;

            return (
              <Box
                key={imageIndex}
                onClick={() => onImageClick?.(imageIndex)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && onImageClick?.(imageIndex)}
                sx={{
                  position: 'relative',
                  flex: 1,
                  borderRadius: bentoTokens.borderRadius.thumbnail,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
              >
                <Image
                  src={url}
                  alt={getAlt(imageIndex)}
                  fill
                  sizes={bentoTokens.imageSizes.splitSide}
                  style={{ objectFit: 'cover' }}
                />
                {isLast && renderSeeAllButton(seeAllSlot, total)}
              </Box>
            );
          })}

          {/* Show map if N=2 (one side image + map) */}
          {images.length === 2 && mapSlot && (
            <Box
              sx={{
                flex: 1,
                borderRadius: bentoTokens.borderRadius.thumbnail,
                overflow: 'hidden',
              }}
            >
              {mapSlot}
            </Box>
          )}
        </Box>
      </GridItem>
    </GridContainer>
  );
}
